import { css } from '@emotion/react'
import TextField from '@mui/material/TextField'
import {
    ChangeEvent,
    ChangeEventHandler,
    FocusEventHandler,
    Fragment,
    KeyboardEventHandler,
    memo,
    useCallback,
    useEffect,
    useState,
    useTransition,
} from 'react'
import { URL_PASSWORD_RESET } from '../../../constants/urls'
import { login, logout } from '../../../mfcLogic/auth'
import { openSignupDrawer } from '../../../state/modals/state.drawer'
import { onInputBlur, onInputFocus } from '../../../state/state.layout'
import { getCookie } from '../../../utilities/cookies'
import i18n from '../../../utilities/i18n'
import { routerPush } from '../../../utilities/router'
import Button from '../../Buttons/Button'
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState'
import Eye from '../../Icons/Eye'
import EyeOff from '../../Icons/EyeOff'
import { muiInputStyles } from '../../common/Forms'
import { getThemeSpacing } from '../../styles/appTheme'
import APP_BREAKPOINTS, { minWindowWidth } from '../../styles/breakpoints'
import { palette } from '../../styles/colors'
import { InlinePulseLoading } from '../InlineLoading'
import Link from '../Link'
import ThirdPartyProviders from './ThirdPartyProviders'
import { eyeIconStyle, validationWarningStyles } from './shared'

const rootStyles = css({
    paddingLeft: getThemeSpacing(1),
    paddingRight: getThemeSpacing(1),
    paddingTop: getThemeSpacing(1),
    paddingBottom: getThemeSpacing(2),
    flexDirection: 'column' as const,
    overflow: 'auto',
    display: 'flex',
    maxWidth: 500,
    [minWindowWidth(APP_BREAKPOINTS.sm)]: {
        padding: getThemeSpacing(2),
    },
})
const loginStyles = css({
    marginBottom: getThemeSpacing(1.5),
    padding: getThemeSpacing(2),
    textTransform: 'none' as const,
})
const logoutStyles = css({
    margin: 'auto',
    width: 120,
})
const subjectStyles = css({
    marginBottom: getThemeSpacing(2),
    textAlign: 'center' as const,
    fontWeight: 500,
    fontSize: '1rem',
})
const formStyles = css({
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',
    '& .MuiTextField-root': {
        width: '100%',
        marginTop: 0,
        '&:first-of-type': {
            marginTop: 0,
        },
    },
})
const signupStyles = css({
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    width: '100%',
    paddingBottom: '10px',
})
const forgotPasswordStyles = css({
    textAlign: 'center',
    padding: '0 0 0.5rem 0',
    width: '100%',
})
const inputContainerStyles = css({ position: 'relative', width: '100%' })

const newAccountStyles = css({
    fontSize: 13,
    letterSpacing: 0.15,
})

interface HelperTextProps {
    error?: string
    username?: string
}

const HelperText = (props: HelperTextProps) => {
    const { error, username } = props
    return error ? (
        <div css={validationWarningStyles}>
            *{' '}
            <span
                data-testid="Login__ValidationError"
                dangerouslySetInnerHTML={{ __html: error }}
            />
            {username && (
                <div
                    css={{ paddingTop: 6 }}
                    data-testid="Login__UnableToLoginNotice"
                >
                    If you are unable to log in, you may{' '}
                    <>
                        <a
                            css={{ color: palette.mfcWarning }}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-mfc-ext="1"
                            href={`https://www.myfreecams.com/php/account.php?request_login_link=1&load=app&style=app&username=${username}`}
                        >
                            click here to receive a login link to your email
                        </a>{' '}
                        or{' '}
                    </>
                    <a
                        css={{ color: palette.mfcWarning }}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-mfc-ext="1"
                        href="https://www.myfreecams.com/php/account.php?request=password&load=app&style=app"
                    >
                        click here to reset your password
                    </a>
                    .
                </div>
            )}
        </div>
    ) : null
}

type MaskedPasswordInputProps = {
    error?: string
    username?: string
    onEnter: KeyboardEventHandler<HTMLInputElement>
    onChange: ChangeEventHandler<HTMLInputElement>
    onFocus?: FocusEventHandler<HTMLInputElement>
    onBlur?: FocusEventHandler<HTMLInputElement>
}

const MaskedPasswordInput = memo((props: MaskedPasswordInputProps) => {
    const [masked, setMasked] = useState(true)
    const { error, onEnter, onChange, onFocus, onBlur, username } = props
    const onToggleMasked = useCallback(() => setMasked(!masked), [masked])

    return (
        <div css={inputContainerStyles}>
            <TextField
                label={i18n.t('login.password')}
                data-testid="Login__PasswordInput"
                type={masked ? 'password' : 'text'}
                autoComplete="current-password"
                onChange={onChange}
                onKeyUp={onEnter}
                onFocus={onFocus}
                onBlur={onBlur}
                error={!!error}
                helperText={<HelperText error={error} username={username} />}
                css={muiInputStyles}
                variant="outlined"
                margin="normal"
                required
            />
            {masked ? (
                <Eye css={eyeIconStyle} onClick={onToggleMasked} />
            ) : (
                <EyeOff css={eyeIconStyle} onClick={onToggleMasked} />
            )}
        </div>
    )
})

const onSignup = () => {
    openSignupDrawer()
}

type LoginProps = {
    subject?: string
    redirect?: string
}

const Login = memo((props: LoginProps) => {
    const isAuthenticated = useAppStateSelector(
        AppStateType.APP,
        'authenticated',
    )
    const [error, setError] = useState<string>()
    const [username, setUsername] = useState<string>()
    const [password, setPassword] = useState<string>()
    const [isPending, startTransition] = useTransition()
    const { subject, redirect } = props

    useEffect(() => {
        setUsername(getCookie('username'))
    }, [])

    const onUsername = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setUsername(e.target.value)
    }, [])

    const onPassword = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value)
    }, [])

    const onLogin = useCallback(
        (event?) => {
            event?.preventDefault()
            startTransition(async () => {
                const { errmsg } = await login(username, password)
                if (errmsg) {
                    setError(errmsg)
                    return
                }
                if (redirect) routerPush(redirect) // Router.push(redirect, redirect, { shallow: true })
                window.location.reload()
            })
        },
        [username, password, redirect],
    )

    const onEnter = useCallback(
        (event) => {
            const { key } = event
            if (key !== 'Enter') return
            event.preventDefault()
            onLogin()
        },
        [onLogin],
    )

    return (
        <div css={rootStyles}>
            {isAuthenticated && (
                <Button css={logoutStyles} onClick={logout} size="large">
                    {i18n.t('login.logout')}
                </Button>
            )}
            {!isAuthenticated && (
                <Fragment>
                    {!!subject && (
                        <div
                            css={subjectStyles}
                            data-testid="Authentication__Prompt"
                        >
                            {i18n.t('login.mustBeLoggedIn')}
                            {subject}.
                        </div>
                    )}
                    <form css={formStyles} noValidate data-testid="Login__Form">
                        <TextField
                            label={i18n.t('login.username')}
                            data-testid="Login__UsernameInput"
                            onChange={onUsername}
                            onFocus={onInputFocus}
                            onBlur={onInputBlur}
                            autoComplete="username"
                            value={username || ''}
                            css={muiInputStyles}
                            error={!!error}
                            variant="outlined"
                            margin="normal"
                            required
                        />
                        <MaskedPasswordInput
                            error={error}
                            username={username}
                            onEnter={onEnter}
                            onChange={onPassword}
                            onFocus={onInputFocus}
                            onBlur={onInputBlur}
                        />
                        <Button
                            data-testid="Login__LoginButton"
                            css={loginStyles}
                            onClick={onLogin}
                            disabled={!username || !password || isPending}
                            size="large"
                        >
                            {!isAuthenticated && isPending && (
                                <InlinePulseLoading active />
                            )}
                            {i18n.t('login.login')}
                        </Button>
                        <div
                            css={forgotPasswordStyles}
                            data-testid="Login__ForgotPasswordLink"
                        >
                            <Link inline mfc href={URL_PASSWORD_RESET}>
                                {i18n.t('login.forgotPassword')}
                            </Link>
                        </div>
                        <div
                            css={signupStyles}
                            data-testid="Login__RegistrationLink"
                        >
                            <span className="typography caption">
                                {i18n.t('login.noAccount')}
                                <Link
                                    onClick={onSignup}
                                    css={newAccountStyles}
                                    actionLink
                                    mfc
                                >
                                    {i18n.t('login.createFreeAccount')}
                                </Link>
                            </span>
                        </div>
                    </form>
                    <ThirdPartyProviders />
                </Fragment>
            )}
        </div>
    )
})

export default Login
